import type { EntityStatus } from '@ms/yammer-data-support';
import { reportError } from '@ms/yammer-telemetry';
import type { RequestInitiator } from '@ms/yammer-telemetry-support';

import type { ManagedApiLifecycle } from '../../api';
import { managedApiAction } from '../../api';
import type { OcpsPolicies } from '../../state/ocps/types';
import type { YammerState } from '../../state/types';
import type { PromiseThunkAction, ThunkDispatch } from '../../types';

import type {
  ClearOcpsPolicyDataAction,
  LoadOcpsPolicyDataFulfilledAction,
  LoadOcpsPolicyDataPendingAction,
  LoadOcpsPolicyDataRejectedAction,
  OcpsPoliciesPayload,
  OcpsPolicyRequestResponse,
  OcpsPolicyResponseValue,
} from './actions.types';
import { OcpsFeedbackPolicyEnabled, OcpsPolicySettingMap } from './actions.types';
import {
  getOcpsFailedRequestCheckInTime,
  getOcpsPolicies,
  getOcpsPolicyCacheVersion,
  getOcpsPolicyCheckInTime,
  getOcpsPolicyDataStatus,
  getOcpsPolicyRequestHash,
} from './selectors';
import { getOcpsPoliciesRequest } from './shared';

/**
 * The OCPS policy data is cached in the browser (see OcpsPolicyData). Any time we decide to store additional settings
 * from OCPS we'll want to bump this value to bust the cache and ensure the latest values are stored.
 */
export const currentOcpsCacheVersion = 3;

const loadOcpsPolicyDataPending = (): LoadOcpsPolicyDataPendingAction => ({
  type: 'OCPS_POLICIES.LOAD_PENDING',
});

const clearOcpsPolicyData = (): ClearOcpsPolicyDataAction => ({
  type: 'OCPS_POLICIES.CLEAR',
});

export const extractPolicyData = (policies: OcpsPolicyResponseValue[] | null): OcpsPolicies => {
  const latestPolicies: OcpsPolicies = {
    SurveyFeedbackFormEnabled: null,
    UserFeedbackFormEnabled: null,
    ConnectedExperiencesEnabled: null,
    AllowScreenshots: null,
    AllowEmailCollection: null,
    AllowLogCollection: null,
  };

  if (policies) {
    policies.forEach((policy: OcpsPolicyResponseValue) => {
      policy.policiesPayload.forEach((row: OcpsPoliciesPayload) => {
        if (OcpsPolicySettingMap[row.settingId]) {
          latestPolicies[OcpsPolicySettingMap[row.settingId]] = row.value === OcpsFeedbackPolicyEnabled;
        }
      });
    });
  }

  return latestPolicies;
};

export const minuteInMilliseconds = 60000;

export const loadOcpsPolicyDataFulfilled = (
  policiesHash: string,
  checkInTime: string,
  policies: OcpsPolicyResponseValue[] | null,
  prevPoliciesHash: string | undefined,
  prevPolicies: OcpsPolicies | undefined
): LoadOcpsPolicyDataFulfilledAction => {
  // if the response contains a policy hash, and it hasn't changed, neither have the policy states
  let updatedPolicies: OcpsPolicies;
  if (policiesHash && policiesHash === prevPoliciesHash) {
    updatedPolicies = prevPolicies!;
  } else {
    updatedPolicies = extractPolicyData(policies);
  }

  const newCheckInTime = Date.now() + parseInt(checkInTime, 10) * minuteInMilliseconds;

  return {
    type: 'OCPS_POLICIES.LOAD_FULFILLED',
    payload: {
      policies: updatedPolicies,
      checkInTime: newCheckInTime,
      requestHash: policiesHash,
      cacheVersion: currentOcpsCacheVersion,
    },
  };
};

const loadOcpsPolicyDataRejected = (error: unknown): LoadOcpsPolicyDataRejectedAction => {
  const checkInTime = Date.now() + 90 * minuteInMilliseconds;

  return {
    type: 'OCPS_POLICIES.LOAD_REJECTED',
    payload: {
      error,
      checkInTime,
    },
    error: true,
  };
};

type LoadOcpsPolicyDataLifecycle = (
  prevPoliciesHash: string | undefined,
  prevPolicies: OcpsPolicies | undefined
) => ManagedApiLifecycle<OcpsPolicyRequestResponse>;
const getLoadOcpsPolicyDataLifecycle: LoadOcpsPolicyDataLifecycle = (prevPoliciesHash, prevPolicies) => ({
  pending: loadOcpsPolicyDataPending,
  fulfilled: ({ policiesHash, checkInTime, value }: OcpsPolicyRequestResponse) =>
    loadOcpsPolicyDataFulfilled(policiesHash, checkInTime, value, prevPoliciesHash, prevPolicies),
  rejected: loadOcpsPolicyDataRejected,
});

type LoadOcpsPolicyData = (host: string, initiator?: RequestInitiator) => PromiseThunkAction<void>;
export const loadOcpsPolicyData: LoadOcpsPolicyData = (host, initiator) => async (dispatch, getState) => {
  const state = getState();
  const requestHash = getOcpsPolicyRequestHash(state);
  const currentPolicies = getOcpsPolicies(state);
  const request = getOcpsPoliciesRequest(host, requestHash, initiator);
  const lifecycle = getLoadOcpsPolicyDataLifecycle(requestHash, currentPolicies);
  const action = managedApiAction({ request, lifecycle });

  try {
    await dispatch(action);
  } catch (error) {
    reportError({ error });
  }
};

export const shouldUpdatePolicyData = (status: EntityStatus, state: YammerState) => {
  const now = Date.now();

  if (status === 'fulfilled') {
    const checkInTime = getOcpsPolicyCheckInTime(state)!;

    if (checkInTime >= now) {
      return false;
    }

    // we also cache the error scenario to throttle requests to their service in case of problems on
    // our end, see: https://dev.azure.com/yammer/engineering/_workitems/edit/317188
  } else if (status === 'rejected') {
    const checkInTime = getOcpsFailedRequestCheckInTime(state)!;

    if (checkInTime >= now) {
      return false;
    }
  }

  return true;
};

export const isOcpsCacheOutOfDate = (status: EntityStatus, state: YammerState) => {
  if (status !== 'fulfilled') {
    return false;
  }

  const cacheVersion = getOcpsPolicyCacheVersion(state);

  return currentOcpsCacheVersion !== cacheVersion;
};

type InitOcpsPolicies = (
  source: string,
  dispatch: ThunkDispatch,
  getState: () => YammerState,
  initiator?: RequestInitiator
) => Promise<void>;
export const initOcpsPolicies: InitOcpsPolicies = async (source, dispatch, getState, initiator) => {
  const state = getState();
  const status = getOcpsPolicyDataStatus(state);

  if (isOcpsCacheOutOfDate(status, state)) {
    dispatch(clearOcpsPolicyData());
  } else if (!shouldUpdatePolicyData(status, state)) {
    return;
  }

  await dispatch(loadOcpsPolicyData(source, initiator));
};
